import { useQuery } from '@tanstack/react-query';
import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React, { useRef } from 'react';
import styled from 'styled-components';

import { Text } from '@column/column-ui-kit';

import { ROUTE as APP_ROUTE } from '~/app/routes';
import { Loading, NotificationList } from '~/components';
import { SetupDeviceMFA } from '~/lib/authn';
import { useNavigate } from '~/lib/navigation';
import { EmailState, UserRepository } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Flex, Headline } from '~/styles';

import { HintLink, Logo, Panel, animateLogo } from './_components';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

const MFAContainer = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  display: flex;
  justify-content: center;
  padding: 0 12px;
  box-sizing: border-box;
`;

const MFAInner = styled.div`
  width: 100%;
  max-width: 600px;
`;

export const PageSetupMFA: React.FC = () => {
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { refetchCurrentUser } = useSessionStore();

  const { data: userData, isPending: isUserPending } = useQuery({
    queryKey: ['user', 'self'],
    queryFn: () => UserRepository.fetchSelf(),
    retry: 1,
  });

  const handleRedirectToDashboard = async () => {
    await refetchCurrentUser();

    if (!logoRef.current || !logoWrapperRef.current || !panelRef.current) {
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
      return;
    }

    // Lock the panel dimensions to prevent layout shifts during animation
    const panel = panelRef.current;
    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;

    animateLogo(logoRef.current, logoWrapperRef.current, panelRef.current, gsap, MotionPathPlugin, () => {
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
    });
  };

  return (
    <>
      <LogoWrapper ref={logoWrapperRef}>
        <AdditionalLogo size="24px" variant="full" ref={logoRef} />
      </LogoWrapper>
      <Panel ref={panelRef}>
        <HiddenLogo size="24px" />
        <Flex flexDirection="column" gap={8} mb={8}>
          <div>
            <Headline fullWidth size="small">
              Setup Multi-Factor Authentication
            </Headline>
            <NotificationList display="page-setup-mfa" spacing="top" />
          </div>
          <Loading isLoading={isUserPending}>
            <>
              {userData && userData.emailState !== EmailState.VERIFIED && (
                <Text size={14} color="foreground" weight="normal">
                  Please verify your email address to setup multi-factor authentication.
                </Text>
              )}
              {userData && userData.emailState === EmailState.VERIFIED && (
                <MFAContainer>
                  <MFAInner>
                    <SetupDeviceMFA onSuccess={handleRedirectToDashboard} notificationDisplay="page-setup-mfa" />
                  </MFAInner>
                </MFAContainer>
              )}
            </>
          </Loading>
          <Flex justifyContent="center" mt={16}>
            <HintLink onClick={() => handleRedirectToDashboard()}>Continue to dashboard</HintLink>
          </Flex>
        </Flex>
      </Panel>
    </>
  );
};
